import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { DealerFeature, EditApiService } from '@hyundai/ng-common-lib';
import { getEditDealerCampaignUrl, isDefined } from '@hyundai/shared-lib';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { DialogService } from '@pixelgenau/ngx-dialog';
import { catchError, filter, from, map, of, switchMap, tap } from 'rxjs';
import { EditFeature } from '..';
import { getContentRefreshAction } from '../../utility';
import { actions } from '../edit';

@Injectable()
export class EditEffects {
    protected readonly actions$ = inject(Actions);
    protected readonly store = inject(Store);
    protected readonly router = inject(Router);

    protected readonly api = inject(EditApiService);
    protected readonly dialogService = inject(DialogService);

    submitEditText$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.submitTextEdit),
            switchMap(({ identifier, value }) =>
                from(this.api.postContentText(identifier, value)).pipe(
                    switchMap(() => [actions.submitTextEditSuccess(), actions.refreshContent({ identifier })]),
                    catchError((error) => of(actions.submitTextEditFailure({ error }))),
                ),
            ),
        );
    });

    submitEditBullet$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.submitBulletEdit),
            switchMap(({ identifier, value }) =>
                from(this.api.postContentBullet(identifier, value)).pipe(
                    switchMap(() => [actions.submitBulletEditSuccess(), actions.refreshContent({ identifier })]),
                    catchError((error) => of(actions.submitBulletEditFailure({ error }))),
                ),
            ),
        );
    });

    submitSetting$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.submitSetting),
            switchMap(({ identifier, value }) =>
                from(this.api.postSetting(identifier, value)).pipe(
                    switchMap(() => [actions.submitSettingSuccess(), actions.refreshContent({ identifier })]),
                    catchError((error) => of(actions.submitSettingFailure({ error }))),
                ),
            ),
        );
    });

    submitImageUpload$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.submitImageUpload),
            switchMap(({ identifier, value }) =>
                from(this.api.postImage(identifier, value)).pipe(
                    switchMap(() => [actions.submitImageUploadSuccess(), actions.refreshContent({ identifier })]),
                    catchError((error) => of(actions.submitImageUploadFailure({ error }))),
                ),
            ),
        );
    });

    submitAboutUsTeasers$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.saveAboutUsTeasers),
            concatLatestFrom(() => [
                this.store.select(EditFeature.state.selectAboutUsTeasersDraft),
                this.store.select(EditFeature.state.selectAboutUsTeaserToRemove),
            ]),
            switchMap(([, teasers, teaserToRemeove]) =>
                from(this.api.postAboutUsTeasers(teasers, teaserToRemeove)).pipe(
                    switchMap(() => [
                        actions.saveAboutUsTeasersSuccess(),
                        actions.refreshContent({ identifier: 'about-us.aboutUsTeaser' }),
                    ]),
                    catchError((error) => of(actions.saveAboutUsTeasersFailure({ error }))),
                ),
            ),
        );
    });

    refreshPage$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(actions.refreshPage),
                tap(() => window.location.reload()),
            );
        },
        {
            dispatch: false,
        },
    );

    refreshContent$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.refreshContent),
            map(({ identifier }) => {
                return getContentRefreshAction(identifier);
            }),
            filter(isDefined),
        );
    });

    loadEmployees$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.loadEmployees),
            switchMap(() =>
                from(this.api.getEmployees()).pipe(
                    map((res) => actions.loadEmployeesSuccess({ employees: res.employees })),
                    catchError((error) => of(actions.loadEmployeesFailure({ error }))),
                ),
            ),
        );
    });

    saveEmployees$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.saveEmployees),
            concatLatestFrom(() => [this.store.select(EditFeature.state.selectEmployees)]),
            switchMap(([, employees]) =>
                from(
                    this.api.saveEmployees(
                        employees.map((employee) => ({
                            ...employee,
                            image: employee.image || '',
                        })),
                    ),
                ).pipe(
                    switchMap(() => [
                        actions.saveEmployeesSuccess(),
                        actions.refreshContent({ identifier: 'about-us.employees' }),
                    ]),
                    catchError((error) => of(actions.saveEmployeesFailure({ error }))),
                ),
            ),
        );
    });

    loadDealerCampaigns$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.loadDealerCampaigns),
            switchMap(() =>
                from(this.api.getDealerCampaigns()).pipe(
                    map((res) => actions.loadDealerCampaignsSuccess({ dealerCampaigns: res.campaigns })),
                    catchError((error) => of(actions.loadDealerCampaignsFailure({ error }))),
                ),
            ),
        );
    });

    loadTextListBullets$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.loadTextListBullets),
            switchMap(({ identifier }) =>
                from(this.api.getTextListBullets(identifier)).pipe(
                    map((res) => actions.loadTextListBulletsSuccess({ bullets: res.bullets })),
                    catchError((error) => of(actions.loadTextListBulletsFailure({ error }))),
                ),
            ),
        );
    });

    loadDealerCampaignDetail$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.loadDealerCampaignDetail),
            switchMap(({ campaignId }) =>
                from(this.api.getDealerCampaignEditDetail(campaignId)).pipe(
                    map((res) => actions.loadDealerCampaignDetailSuccess({ campaign: res.campaign })),
                    catchError((error) => of(actions.loadDealerCampaignDetailFailure({ error }))),
                ),
            ),
        );
    });

    upsertDealerCampaign$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.upsertDealerCampaign),
            concatLatestFrom(() => [this.store.select(DealerFeature.state.selectLoadedDealer)]),
            switchMap(([action, dealer]) =>
                from(this.api.upsertDealerCampaign(action.campaign)).pipe(
                    map((res) => {
                        if (action.campaign.id === 0) {
                            this.router.navigate([getEditDealerCampaignUrl(dealer.slug, res.campaign.id)]);
                        }
                        return actions.upsertDealerCampaignSuccess({ campaign: res.campaign });
                    }),
                    catchError((error) => of(actions.upsertDealerCampaignFailure({ error }))),
                ),
            ),
        );
    });

    deleteDealerCampaign$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.deleteDealerCampaign),
            switchMap(({ campaignId }) =>
                from(this.api.deleteDealerCampaign(campaignId)).pipe(
                    switchMap(() => {
                        return [actions.deleteDealerCampaignSuccess(), actions.loadDealerCampaigns()];
                    }),
                    catchError((error) => of(actions.deleteDealerCampaignFailure({ error }))),
                ),
            ),
        );
    });

    loadContentListBullets$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.loadServiceList),
            switchMap(() =>
                from(this.api.getServiceList()).pipe(
                    map((res) => actions.loadServiceListSuccess({ services: res.services })),
                    catchError((error) => of(actions.loadServiceListFailure({ error }))),
                ),
            ),
        );
    });

    loadList$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.loadHomeTeaserList),
            switchMap(() =>
                from(this.api.getHomeTeaserList()).pipe(
                    map((res) => actions.loadHomeTeaserListSuccess({ teasers: res.teasers })),
                    catchError((error) => of(actions.loadHomeTeaserListFailure({ error }))),
                ),
            ),
        );
    });

    saveTextList$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.saveTextList),
            switchMap(({ identifier, value }) =>
                from(this.api.saveTextList(identifier, value)).pipe(
                    switchMap(() => [actions.saveTextListSuccess(), actions.refreshContent({ identifier })]),
                    catchError((error) => of(actions.saveTextListFailure({ error }))),
                ),
            ),
        );
    });

    saveImageTextContent$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.saveImageTextContent),
            switchMap(({ identifier, value }) =>
                from(this.api.saveImageTextContent(identifier, value)).pipe(
                    switchMap(() => [actions.saveImageTextContentSuccess(), actions.refreshContent({ identifier })]),
                    catchError((error) => of(actions.saveImageTextContentFailure({ error }))),
                ),
            ),
        );
    });
}
